/**
 * Copyright (c) Microsoft. All rights reserved.
 */
import { getId } from '@fluentui/react';
import { default as React } from 'react';
import { defineMessages } from 'react-intl';
import { matchPath, Prompt } from 'react-router';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { ConfirmLeaveEvent, ConfirmStayEvent } from 'src/core/types/ConfirmLeaveEvent';
import { getEventBusV2 } from 'src/core/utils/eventBusV2';

type UnsavedChangesPromptProps = {
    /**
     * The path route where the prompt is rendered.
     */
    promptPath?: string;

    /**
     * A boolean indicates whether the page has unsaved changes or not.
     */
    isPageDirty: boolean;

    /**
     * An optional callback to be run on leaving page.
     */
    onLeaveCb?: () => void;

    /**
     * An optional callback to be run on staying in the page.
     */
    onStayCb?: () => void;
};

const messages = defineMessages({
    unSavedChangesModalTitle: {
        id: 'core.unsavedChangesPrompt.unSavedChangesModalTitle',
        defaultMessage: 'Are you sure you want to leave the page?'
    },
    unSavedChangesModalBody: {
        id: 'core.unsavedChangesPrompt.unSavedChangesModalBody',
        defaultMessage: 'You have unsaved changes, You can leave to discard your changes, or cancel to continue editing.'
    },
    unSavedChangesModalSave: {
        id: 'core.unsavedChangesPrompt.unSavedChangesModalSave',
        defaultMessage: 'Leave'
    },
    unSavedChangesModalDiscard: {
        id: 'core.unsavedChangesPrompt.unSavedChangesModalDiscard',
        defaultMessage: 'Cancel'
    }
});

export const UnsavedChangesPrompt = (props: UnsavedChangesPromptProps) => {
    const { onLeaveCb, onStayCb, promptPath, isPageDirty } = props;
    const { localize } = useLocalization();
    const [unsavedChangesPromptId] = React.useState(getId('unsavedChangesPromptId'));

    const eventBusV2 = getEventBusV2();

    const unSavedChangesModalMessages = {
        id: unsavedChangesPromptId,
        leaveAnyway: false,
        title: localize(messages.unSavedChangesModalTitle),
        description: localize(messages.unSavedChangesModalBody),
        leaveButtonText: localize(messages.unSavedChangesModalSave),
        cancelButtonText: localize(messages.unSavedChangesModalDiscard)
    };

    React.useEffect(
        () =>
            eventBusV2.on<ConfirmLeaveEvent>({
                kind: 'confirmLeave',
                callback: params => {
                    if (params.id === unsavedChangesPromptId) {
                        onLeaveCb?.();
                    }
                }
            }),
        [onLeaveCb]
    );

    React.useEffect(
        () =>
            eventBusV2.on<ConfirmStayEvent>({
                kind: 'confirmStay',
                callback: params => {
                    if (params.id === unsavedChangesPromptId) {
                        onStayCb?.();
                    }
                }
            }),
        [onStayCb]
    );

    return (
        <Prompt
            when={isPageDirty}
            message={loc => {
                if (matchPath(loc.pathname, promptPath)) {
                    return;
                }

                return JSON.stringify(unSavedChangesModalMessages);
            }}
        />
    );
};
